import { runInAction } from 'mobx';

import { sendParts, withVideo } from '@/lib/voltrSend';
import { assertUploadableSize, sha256Of } from '@/lib/voltrVideo';
import { voltrService } from '@/services/voltr';
import { dealsStore } from '@/stores/deals.store';
import type { UploadContext, UploadItem } from '@/stores/voltrUploadTypes';

/**
 * Hash the video from disk, ask the server for part URLs, and PUT the bytes
 * straight to Voltr (asking again for fresh URLs when a part fails). Same
 * file again resumes its open job. The file is always closed. Returns the
 * job id; `assertLive` throws once a logout or Cancel disowned the work.
 */
export async function sendVideo(
  item: UploadItem,
  context: UploadContext,
  assertLive: () => void,
  onProgress: (sent: number) => void,
  signal: AbortSignal,
): Promise<string> {
  runInAction(() => Object.assign(item, { phase: 'preparing', error: null, sentBytes: 0, jobId: null }));
  assertUploadableSize(item.video.sizeBytes);
  return withVideo(item.video, async (source) => {
    assertLive();
    assertUploadableSize(source.size);
    runInAction(() => void (item.totalBytes = source.size));
    const sha256 = item.sha256 ?? (await sha256Of(source, signal));
    runInAction(() => void (item.sha256 = sha256));
    const file = { fileName: item.video.fileName, mimeType: item.video.mimeType, sizeBytes: source.size, sha256 };
    const create = async () => {
      assertLive();
      const created = await voltrService.createUpload(context.actionId, file, context.rightsVersion, context.replaces);
      assertLive();
      dealsStore.applyAction(created.action);
      runInAction(() => Object.assign(item, { phase: 'sending', serverJobId: created.upload.id }));
      return created.upload;
    };
    return sendParts(create, source, (sent) => runInAction(() => onProgress(sent)), signal);
  });
}
